import React from 'react';
import { motion } from 'framer-motion';
import { Prompt } from '../types';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/helpers';
import ImageHoverPreview from './ImageHoverPreview';

interface PromptCardProps {
  prompt: Prompt;
  index: number;
  onView: () => void;
  onCopy: () => void;
  onEdit?: () => void;
  onDelete?: (id: number) => void;
  onTogglePublic?: (id: number) => void;
  onViewVersions?: () => void;
}

const PromptCard: React.FC<PromptCardProps> = ({ 
  prompt, 
  index, 
  onView, 
  onCopy, 
  onEdit, 
  onDelete, 
  onTogglePublic,
  onViewVersions
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopy();
    toast.success('提示词已复制到剪贴板！');
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && window.confirm('确定要删除这个提示词吗？此操作不可撤销。')) {
      onDelete(prompt.id);
    }
  };

  const handleTogglePublic = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTogglePublic) {
      onTogglePublic(prompt.id);
    }
  };

  const handleViewVersions = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewVersions) {
      onViewVersions();
    }
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const shouldShowExpanded = prompt.content.length > 200;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onClick={onView}
      className="w-full bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-3 pr-4 leading-tight whitespace-pre-wrap">
            {prompt.title}
          </h3>
          {/* 公开状态指示器 */}
          {prompt.isPublic && (
            <div className="flex items-center mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <i className="fas fa-globe text-green-600 mr-1"></i>
                已分享到广场
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleCopy}
            title="复制提示词内容"
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <i className="fas fa-copy"></i>
          </button>
          {onViewVersions && (
            <button
              onClick={handleViewVersions}
              title="查看版本历史"
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-purple-600 transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg"
            >
              <i className="fas fa-history"></i>
            </button>
          )}
          {onTogglePublic && (
            <button
              onClick={handleTogglePublic}
              title={prompt.isPublic ? "从广场移除" : "分享到广场"}
              className={`p-2 transition-colors rounded-lg ${
                prompt.isPublic 
                  ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
              }`}
            >
              <i className={`fas ${prompt.isPublic ? 'fa-globe' : 'fa-share-alt'}`}></i>
            </button>
          )}
          {onEdit && (
            <button
              onClick={handleEdit}
              title="编辑提示词"
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
            >
              <i className="fas fa-edit"></i>
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              title="删除提示词"
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <p className={`text-gray-600 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap ${!isExpanded && shouldShowExpanded ? 'line-clamp-4' : ''}`}>
          {prompt.content}
        </p>
        {shouldShowExpanded && (
          <button
            onClick={handleToggleExpanded}
            className="text-primary hover:text-primary-dark text-xs mt-2 font-medium transition-colors"
          >
            {isExpanded ? (
              <>
                <i className="fas fa-chevron-up mr-1"></i>
                收起
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down mr-1"></i>
                展开全部
              </>
            )}
          </button>
        )}
      </div>

      {/* 效果图片预览 */}
      {prompt.images && prompt.images.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          {prompt.images.slice(0, 3).map((imageUrl, index) => (
            <ImageHoverPreview
              key={index}
              imageUrl={imageUrl}
              alt={`效果图 ${index + 1}`}
              thumbnailClassName="w-16 h-16"
              previewSize={320}
            />
          ))}
          {prompt.images.length > 3 && (
            <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm font-medium">
              +{prompt.images.length - 3}
            </div>
          )}
        </div>
      )}

      {/* 标签展示 */}
      {prompt.tags && prompt.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {prompt.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary bg-opacity-10 text-primary border border-primary border-opacity-20"
            >
              <i className="fas fa-tag mr-1 text-xs"></i>
              {tag}
            </span>
          ))}
          {prompt.tags.length > 3 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              +{prompt.tags.length - 3}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            <i className="fas fa-folder mr-1"></i>
            {prompt.category}
          </span>
          {prompt.versionCount && prompt.versionCount > 1 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
              <i className="fas fa-history mr-1"></i>
              {prompt.versionCount} 版本
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(prompt.createdAt)}</span>
      </div>
    </motion.div>
  );
};

export default PromptCard; 